import { api, defineWebModule } from '@itc/sdk';
import { getCounts, searchDonations } from '../shared/endpoints.ts';
import { ALL_TOPIC, REQUESTS_TOPIC, meTopic } from '../shared/topics.ts';

const drafts = () => import('./reports.ts');
const images = () => import('./cards.ts');

// Donations in the shell (design §7.5, plan 2C): the Donations page (Chunk 5), the
// Requests page with its Incoming tab (Chunk 6), and on Home, Sierra's Bridge panels
// for requests, incoming and donations, with live badges on the nav (Chunk 7). Any
// logged-in user may open Donations: members see their own donations, receivers
// (view_all) everything, and a donor a receiver recorded for sees theirs whatever
// they hold. The server decides what each sees.
export default defineWebModule({
  name: 'donations',
  title: 'Donations',
  search: { permissions: ['donations.view_requests', 'donations.view_all', 'donations.manage_requests', 'donations.submit', 'donations.view_own'], run: async (q, signal) => (await api(searchDonations, { query: { q } }, { signal })).hits },
  cards: [
    { id: 'donations.donors', label: 'Donor thanks', permission: 'donations.view_all', spec: async () => (await images()).donorsImage() },
    { id: 'donations.requests', label: 'Open requests', permission: 'donations.view_requests', spec: async () => (await images()).requestsImage() },
    {
      id: 'donations.bankdonors',
      label: 'Bank donors',
      permission: 'donations.view_all',
      variants: [
        { value: '30', label: 'Last 30 days' },
        { value: '90', label: 'Last 90 days' },
        { value: 'all', label: 'All time' },
      ],
      spec: async (variant) => (await images()).bankDonorsImage(variant),
    },
  ],
  reports: [{ type: 'donors', permission: 'donations.view_all', draft: async () => (await drafts()).donors() }],
  routes: [
    { path: '/donations', component: () => import('./pages/DonationsPage.vue'), meta: { permission: 'authenticated', title: 'Donations' } },
    // Requests and Incoming: members see Active requests; Incoming is for view_all.
    { path: '/donations/requests', component: () => import('./pages/RequestsPage.vue'), meta: { permission: 'donations.view_requests', title: 'Requests' } },
  ],
  nav: [
    {
      label: 'Donations',
      to: '/donations',
      permission: 'authenticated',
      icon: 'donations',
      // R15: what waits on you: donations you can accept, credit you can confirm or approve, failed settlements you accepted.
      badge: {
        permission: 'authenticated',
        count: async () => (await api(getCounts)).waiting,
        topics: (userId, can) => [meTopic(userId), ...(can('donations.view_all') ? [ALL_TOPIC] : [])],
        label: (count) => `${String(count)} waiting on you`,
      },
    },
    {
      label: 'Requests',
      to: '/donations/requests',
      permission: 'donations.view_requests',
      icon: 'requests',
      // R15: drafts waiting for a coordinator's review (drafts go out on donations.all, not donations.requests).
      badge: {
        permission: 'donations.manage_requests',
        count: async () => (await api(getCounts)).drafts,
        topics: (_userId, can) => [can('donations.view_all') ? ALL_TOPIC : REQUESTS_TOPIC],
        label: (count) => `${String(count)} to review`,
      },
    },
  ],
  // Sierra's Bridge panels for donations (lines 891–937), for those who may see each.
  admin: [{ label: 'Materials', path: '/admin/donations/materials', component: () => import('./admin/Materials.vue'), permission: 'donations.manage_materials' }],
  widgets: [
    { id: 'donations.requests', title: 'Requests', permission: 'donations.view_requests', component: () => import('./widgets/RequestsWidget.vue') },
    { id: 'donations.incoming', title: 'Incoming', permission: 'donations.view_all', component: () => import('./widgets/IncomingWidget.vue') },
    { id: 'donations.recent', title: 'Recent donations', permission: 'donations.view_all', component: () => import('./widgets/RecentDonationsWidget.vue') },
    { id: 'donations.board', title: 'Top donors', permission: 'donations.view_requests', component: () => import('./widgets/DonorBoardWidget.vue') },
  ],
});
